/**
 * Whether a captured frame is a photograph of a lit face at a sensible distance
 * (docs/5 § D260805-1206). The thresholds are `CAPTURE_CHECKS`; the detector is
 * `lib/face-detector`; this module is the rules and the words.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * EVERYTHING IS MEASURED ON THE SQUARE THAT IS ACTUALLY KEPT. `squareFrom` takes
 * the centred square of the camera frame, and that one canvas is both what gets
 * sent and what gets measured. Judging the full 4:3 frame would be judging pixels
 * the stored square throws away — a face perfectly placed in the corner of a
 * landscape frame is not in the photograph at all. (It said "the round avatar"
 * until docs/5 § D260805-1523; the square is what the crop was always about, and the frame
 * it is worn in is now a stamp.)
 *
 * THE CHECKS RUN IN THE ORDER A PERSON CAN ACT ON, and distance comes first for a
 * mechanical reason as well as a human one: the exposure numbers are read from
 * the pixels inside the face box, and a face thirty pixels tall does not have
 * enough of them to be sure about. Fix the distance and the light verdict becomes
 * worth trusting.
 *
 * DARKNESS IS DIAGNOSED BEFORE ABSENCE. In an unlit room the detector finds
 * nothing, and the honest reading of "no face in a dark frame" is *dark* rather
 * than *no face*. Reporting the absence would send somebody looking for a
 * framing problem in a room where the answer is a lamp. The mirror-image test for
 * a blown-out frame is NOT here, and `verdictFor` says why at the point where it
 * would otherwise sit.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { CAPTURE_CHECKS } from '@dove/shared';
import { loadFaceDetector, type Face } from './face-detector';

/**
 * The edge of the canvas the checks run on.
 *
 * NOT A PRODUCT PARAMETER, so not in `packages/shared` — it changes nothing about
 * what passes, only how much work the measuring costs. Every threshold is a
 * fraction of the edge or a luma value, and both are scale-invariant. The model's
 * own input is 128px square, so anything above that is thrown away by the resize
 * inside it; this is twice that, because the exposure statistics are read at this
 * resolution rather than the model's and a face box at 128 is coarse.
 */
export const ANALYSIS_EDGE = 256;

/** What is wrong with the frame. One at a time, in the order checked. */
export type CaptureFault =
  | 'dark'
  | 'no_face'
  | 'far'
  | 'near'
  | 'off_centre'
  | 'bright';

export type CaptureCheck =
  | { verdict: 'ok' }
  | { verdict: 'fault'; fault: CaptureFault }
  /** The detector could not be had. The photograph goes through — see
   *  `loadFaceDetector`, where that decision is argued. */
  | { verdict: 'unchecked' };

/**
 * The line shown for each fault.
 *
 * SIX LABELS, NOT SIX EXPLANATIONS. Each one is the shortest true instruction:
 * what to do, or what is wrong when there is nothing to do about it. No sentence
 * here says why a photograph needs light, none apologises, and none reassures —
 * a status line that teaches is the regression CLAUDE.md names first.
 *
 * "Fit your face in the frame" rather than "no face detected", because the
 * second is the machine's account of its own failure and the first is the only
 * thing the person can act on. It said *circle* until docs/5 § D260805-1523: the
 * viewfinder is the stamp now, and a line naming a shape that is not on screen is
 * worse than a vague one. This is also the line somebody gets when the
 * detector is simply wrong about them, which is the case that decides the
 * wording: it must read as a framing note, never as *you are not a person*.
 */
export const CAPTURE_FAULT_LABEL: Record<CaptureFault, string> = {
  dark: 'More light',
  no_face: 'Fit your face in the frame',
  far: 'Move closer',
  near: 'Move back',
  off_centre: 'Centre your face',
  bright: 'Too bright',
};

/** Anything with a frame in it: the live preview, a decoded photograph from the
 *  platform camera, or a canvas already cropped from one of those. */
export type CaptureSource =
  | HTMLVideoElement
  | HTMLCanvasElement
  | HTMLImageElement
  | ImageBitmap;

/** Intrinsic pixels, which for a `<video>` is not its layout size. */
function sizeOf(source: CaptureSource): { width: number; height: number } {
  if (source instanceof HTMLVideoElement) {
    return { width: source.videoWidth, height: source.videoHeight };
  }
  if (source instanceof HTMLImageElement) {
    return { width: source.naturalWidth, height: source.naturalHeight };
  }
  return { width: source.width, height: source.height };
}

/**
 * The centred square of a source, at most `edge` on a side.
 *
 * NEVER UPSCALED — `edge` is a ceiling, so a small source stays its own size
 * rather than being interpolated up to look like more detail than it has.
 *
 * NOT MIRRORED, matching what is stored. The preview is mirrored in CSS because
 * everybody frames themselves in a mirror, but the photograph other people see is
 * the way round the camera saw it. The checks are unaffected either way: they use
 * the box's height and the *distance* of its centre from the middle, and both
 * survive a flip. Anything that ever draws the box on top of the preview will not.
 */
export function squareFrom(
  source: CaptureSource,
  edge: number,
): HTMLCanvasElement | null {
  const { width, height } = sizeOf(source);
  if (!width || !height) return null;

  const side = Math.min(width, height);
  const out = Math.max(1, Math.round(Math.min(edge, side)));

  const canvas = document.createElement('canvas');
  canvas.width = out;
  canvas.height = out;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.drawImage(
    source,
    Math.round((width - side) / 2),
    Math.round((height - side) / 2),
    side,
    side,
    0,
    0,
    out,
    out,
  );
  return canvas;
}

/** Rec.709 luma on the sRGB values as stored, which is the brightness a person
 *  sees rather than a physical intensity — the right one for "is this lit". */
function luma(r: number, g: number, b: number): number {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

type Box = { x: number; y: number; width: number; height: number };

/**
 * The 90th-percentile luma of a region, and how much of it is clipped.
 *
 * A PERCENTILE RATHER THAN A MEAN, argued in `CAPTURE_CHECKS`: an average
 * measures skin as much as light, so a floor under it fails dark-skinned faces in
 * rooms where light-skinned faces pass. The bright parts of a lit face are bright
 * whatever the face — a 256-bin histogram is exact enough to find them and costs
 * one pass.
 */
function exposureOf(
  pixels: ImageData,
  box: Box,
): { highlight: number; clipped: number } | null {
  const x0 = Math.max(0, Math.floor(box.x));
  const y0 = Math.max(0, Math.floor(box.y));
  const x1 = Math.min(pixels.width, Math.ceil(box.x + box.width));
  const y1 = Math.min(pixels.height, Math.ceil(box.y + box.height));
  if (x1 <= x0 || y1 <= y0) return null;

  /* `!` on every read, as in `three/bird.ts`: these are fixed-length typed arrays
     indexed inside their own bounds, and `?? 0` in a per-pixel loop would be a
     branch per channel to guard against something the loop makes impossible. */
  const d = pixels.data;
  const histogram = new Uint32Array(256);
  let count = 0;
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      const i = (y * pixels.width + x) * 4;
      histogram[Math.round(luma(d[i]!, d[i + 1]!, d[i + 2]!))]!++;
      count++;
    }
  }
  if (!count) return null;

  let seen = 0;
  let highlight = 255;
  const target = count * 0.9;
  for (let v = 0; v < 256; v++) {
    seen += histogram[v]!;
    if (seen >= target) {
      highlight = v;
      break;
    }
  }

  let clipped = 0;
  for (let v = CAPTURE_CHECKS.LUMA_CLIPPED_AT; v < 256; v++) clipped += histogram[v]!;

  return { highlight, clipped: clipped / count };
}

/** The verdict for an already-square canvas and the face found in it, if any.
 *  Separated from `inspect` so the rules can be reasoned about — and calibrated —
 *  without a camera or a model in the way. */
export function verdictFor(
  pixels: ImageData,
  face: Face | null,
): { verdict: 'ok' } | { verdict: 'fault'; fault: CaptureFault } {
  const edge = pixels.width;

  if (!face) {
    /*
     * DARKNESS BEFORE ABSENCE — and darkness only. Measured over the whole square,
     * because with no box there is nowhere else to look.
     *
     * THE SYMMETRIC TEST FOR BLOWOUT WAS HERE AND IS DELIBERATELY GONE. It read
     * well and it was wrong, which the harness demonstrated with a photograph of
     * the wall behind somebody's head: mostly clipped, no face, and reported as
     * *Too bright* when the honest answer is that there is no face in it. The
     * asymmetry is real rather than an oversight. A frame whose 90th percentile is
     * under the floor is a frame in which almost nothing is visible, so the dark IS
     * the reason no face was found. Clipping is not that: white walls, windows,
     * skies and pale shirts all clip while remaining perfectly legible, so
     * "over-exposed" cannot be inferred from it in the way "unlit" can. Sending
     * somebody who photographed their ceiling off to find a lamp to turn *down* is
     * worse than telling them to put their face in the frame.
     *
     * A face lost to genuine backlight therefore reports `no_face`, which is at
     * least an instruction they can act on. Blowout is still caught whenever a face
     * IS found, where the measurement is over the face and means what it says.
     */
    const overall = exposureOf(pixels, { x: 0, y: 0, width: edge, height: edge });
    if (overall && overall.highlight < CAPTURE_CHECKS.FACE_MIN_HIGHLIGHT_LUMA) {
      return { verdict: 'fault', fault: 'dark' };
    }
    return { verdict: 'fault', fault: 'no_face' };
  }

  const box: Box = {
    x: face.box.xMin,
    y: face.box.yMin,
    width: face.box.width,
    height: face.box.height,
  };

  const height = box.height / edge;
  if (height < CAPTURE_CHECKS.FACE_MIN_HEIGHT_FRACTION) {
    return { verdict: 'fault', fault: 'far' };
  }
  if (height > CAPTURE_CHECKS.FACE_MAX_HEIGHT_FRACTION) {
    return { verdict: 'fault', fault: 'near' };
  }

  /*
   * DOES THE FACE FIT IN THE CIRCLE INSCRIBED IN THE KEPT SQUARE. `reach` is how far
   * its furthest extreme gets, as a multiple of that circle's radius — 1 means
   * touching it. The frame the photograph is WORN in is a stamp rather than a circle
   * (docs/5 § D260805-1523); `FACE_MAX_CIRCLE_REACH` carries the argument for why the rule
   * did not move with the shape.
   *
   * The four extremes, not the four corners of the box. A face is roughly an
   * ellipse inside that box, so it is the chin, the forehead and the ears that the
   * mask can cut; the box's corners are cheek-and-background and stick out of an
   * inscribed circle even for a perfectly centred face. Testing them would refuse
   * ordinary photographs for a geometry the photograph does not have.
   */
  const radius = edge / 2;
  const reach =
    Math.max(
      Math.abs(box.x + box.width / 2 - radius) + box.width / 2,
      Math.abs(box.y + box.height / 2 - radius) + box.height / 2,
    ) / radius;
  if (reach > CAPTURE_CHECKS.FACE_MAX_CIRCLE_REACH) {
    return { verdict: 'fault', fault: 'off_centre' };
  }

  const exposure = exposureOf(pixels, box);
  if (exposure) {
    if (exposure.highlight < CAPTURE_CHECKS.FACE_MIN_HIGHLIGHT_LUMA) {
      return { verdict: 'fault', fault: 'dark' };
    }
    if (exposure.clipped > CAPTURE_CHECKS.FACE_MAX_CLIPPED_FRACTION) {
      return { verdict: 'fault', fault: 'bright' };
    }
  }

  return { verdict: 'ok' };
}

/**
 * Look at a square canvas and say what is wrong with it.
 *
 * The caller owns the canvas and should hand over the same one it intends to
 * send — checking one frame and uploading another is a gate that does not gate.
 */
export async function inspect(square: HTMLCanvasElement): Promise<CaptureCheck> {
  const detector = await loadFaceDetector();
  if (!detector) return { verdict: 'unchecked' };

  const ctx = square.getContext('2d');
  if (!ctx) return { verdict: 'unchecked' };

  try {
    const faces = await detector.estimateFaces(square, { flipHorizontal: false });
    const pixels = ctx.getImageData(0, 0, square.width, square.height);
    return verdictFor(pixels, faces[0] ?? null);
  } catch {
    /* An inference that throws is our problem, not the photographer's. */
    return { verdict: 'unchecked' };
  }
}
